// Canvas/pixel utilities: background sampling for text edits, image
// normalisation, signature trimming, and the compare-mode raster diff.

/** The dominant colour of the page raster inside `bbox` — i.e. what the
 *  text sits ON. Glyphs are a minority of pixels in a line box, so the
 *  modal colour is the background; without this, editing white-on-dark
 *  text puts white text in a white input. Returns null if unsampleable. */
export function sampleRasterBackground(
  canvas: HTMLCanvasElement | null,
  bbox: [number, number, number, number],
  pageWidth: number
): string | null {
  if (!canvas || !canvas.width || !canvas.height) return null;
  const k = canvas.width / pageWidth; // raster px per PDF point
  const x = Math.max(0, Math.floor(bbox[0] * k));
  const y = Math.max(0, Math.floor(bbox[1] * k));
  const w = Math.min(canvas.width - x, Math.ceil((bbox[2] - bbox[0]) * k));
  const h = Math.min(canvas.height - y, Math.ceil((bbox[3] - bbox[1]) * k));
  if (w < 2 || h < 2) return null;
  try {
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return null;
    const { data } = ctx.getImageData(x, y, w, h);
    // 5-bit-per-channel histogram: enough to separate ink from paper
    // without splitting anti-aliased gradients into noise.
    const counts = new Map<number, number>();
    for (let i = 0; i < data.length; i += 4) {
      const key = ((data[i]! >> 3) << 10) | ((data[i + 1]! >> 3) << 5) | (data[i + 2]! >> 3);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    let bestKey = -1;
    let bestCount = 0;
    for (const [key, count] of counts) {
      if (count > bestCount) {
        bestCount = count;
        bestKey = key;
      }
    }
    if (bestKey < 0) return null;
    const to8 = (v: number) => Math.min(255, (v << 3) | 4);
    const r = to8((bestKey >> 10) & 31);
    const g = to8((bestKey >> 5) & 31);
    const b = to8(bestKey & 31);
    return `#${[r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("")}`;
  } catch {
    return null; // tainted canvas / zero-size raster
  }
}

/** Normalise any user image to a PNG data URL, capped at 1600px on the
 *  long edge — guarantees a format MuPDF's Image can decode. */
export async function imageFileToDataUrl(
  file: Blob
): Promise<{ dataUrl: string; width: number; height: number }> {
  const bitmap = await createImageBitmap(file);
  const cap = 1600;
  const ratio = Math.min(1, cap / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * ratio));
  const h = Math.max(1, Math.round(bitmap.height * ratio));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  canvas.getContext("2d")!.drawImage(bitmap, 0, 0, w, h);
  bitmap.close();
  return { dataUrl: canvas.toDataURL("image/png"), width: w, height: h };
}

/** Crop a canvas to its non-transparent pixels (plus padding). Returns
 *  null when nothing was drawn. */
export function trimCanvas(
  canvas: HTMLCanvasElement,
  pad = 8
): { dataUrl: string; width: number; height: number } | null {
  const ctx = canvas.getContext("2d")!;
  const { data, width, height } = ctx.getImageData(0, 0, canvas.width, canvas.height);
  let minX = width, minY = height, maxX = -1, maxY = -1;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (data[(y * width + x) * 4 + 3]! > 8) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return null;
  const w = maxX - minX + 1 + pad * 2;
  const h = maxY - minY + 1 + pad * 2;
  const out = document.createElement("canvas");
  out.width = w;
  out.height = h;
  out.getContext("2d")!.drawImage(canvas, minX - pad, minY - pad, w, h, 0, 0, w, h);
  return { dataUrl: out.toDataURL("image/png"), width: w, height: h };
}

export interface DiffBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Pixel-diff two same-scale rasters: changed fraction + clustered
 *  boxes (8px grid cells marked dirty, flood-merged into rectangles).
 *  Size mismatches count the non-overlapping stripes as changed. */
export function diffRasters(
  a: { width: number; height: number; data: Uint8ClampedArray },
  b: { width: number; height: number; data: Uint8ClampedArray },
  threshold = 24
): { changed: number; changedPx: number; boxes: DiffBox[] } {
  const W = Math.max(a.width, b.width);
  const H = Math.max(a.height, b.height);
  const CELL = 8;
  const gw = Math.ceil(W / CELL);
  const gh = Math.ceil(H / CELL);
  const dirty = new Uint8Array(gw * gh);
  let changedPx = 0;
  for (let y = 0; y < H; y += 1) {
    const inA = y < a.height;
    const inB = y < b.height;
    for (let x = 0; x < W; x += 1) {
      let diff: boolean;
      if (x < a.width && inA && x < b.width && inB) {
        const ia = (y * a.width + x) * 4;
        const ib = (y * b.width + x) * 4;
        diff =
          Math.abs(a.data[ia]! - b.data[ib]!) > threshold ||
          Math.abs(a.data[ia + 1]! - b.data[ib + 1]!) > threshold ||
          Math.abs(a.data[ia + 2]! - b.data[ib + 2]!) > threshold;
      } else {
        // Only one document has pixels here (different page sizes) —
        // treat as changed only if that pixel carries ink.
        const src = x < a.width && inA ? a : x < b.width && inB ? b : null;
        if (!src) {
          diff = false;
        } else {
          const i = (y * src.width + x) * 4;
          diff = src.data[i]! < 240 || src.data[i + 1]! < 240 || src.data[i + 2]! < 240;
        }
      }
      if (diff) {
        changedPx += 1;
        dirty[Math.floor(y / CELL) * gw + Math.floor(x / CELL)] = 1;
      }
    }
  }
  // merge dirty cells into rectangles: greedy row-run + vertical merge
  const boxes: DiffBox[] = [];
  const used = new Uint8Array(gw * gh);
  for (let gy = 0; gy < gh; gy += 1) {
    for (let gx = 0; gx < gw; gx += 1) {
      if (!dirty[gy * gw + gx] || used[gy * gw + gx]) continue;
      let x1 = gx;
      while (x1 + 1 < gw && dirty[gy * gw + x1 + 1] && !used[gy * gw + x1 + 1]) x1 += 1;
      let y1 = gy;
      const rowFull = (yy: number) => {
        for (let xx = gx; xx <= x1; xx += 1) {
          if (!dirty[yy * gw + xx] || used[yy * gw + xx]) return false;
        }
        return true;
      };
      while (y1 + 1 < gh && rowFull(y1 + 1)) y1 += 1;
      for (let yy = gy; yy <= y1; yy += 1) {
        for (let xx = gx; xx <= x1; xx += 1) used[yy * gw + xx] = 1;
      }
      boxes.push({
        x: gx * CELL,
        y: gy * CELL,
        w: (x1 - gx + 1) * CELL,
        h: (y1 - gy + 1) * CELL,
      });
    }
  }
  return { changed: changedPx / (W * H), changedPx, boxes };
}

/** Red/cyan onion skin: ink only in A shows red, only in B cyan, and
 *  shared ink goes dark — instantly readable change highlighting. */
export function blendRasters(
  a: { width: number; height: number; data: Uint8ClampedArray },
  b: { width: number; height: number; data: Uint8ClampedArray }
): ImageData {
  const W = Math.max(a.width, b.width);
  const H = Math.max(a.height, b.height);
  const out = new Uint8ClampedArray(W * H * 4);
  for (let y = 0; y < H; y += 1) {
    for (let x = 0; x < W; x += 1) {
      const o = (y * W + x) * 4;
      let aInk = 0;
      let bInk = 0;
      if (x < a.width && y < a.height) {
        const i = (y * a.width + x) * 4;
        aInk = 255 - Math.round((a.data[i]! + a.data[i + 1]! + a.data[i + 2]!) / 3);
      }
      if (x < b.width && y < b.height) {
        const i = (y * b.width + x) * 4;
        bInk = 255 - Math.round((b.data[i]! + b.data[i + 1]! + b.data[i + 2]!) / 3);
      }
      out[o] = 255 - bInk; // B's ink drains red → cyan
      out[o + 1] = 255 - aInk; // A's ink drains green+blue → red
      out[o + 2] = 255 - aInk;
      out[o + 3] = 255;
    }
  }
  return new ImageData(out, W, H);
}
